import type { MouseEventHandler } from 'react'
import { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import FormatCurrency from 'components/Common/FormatCurrency'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Pill from 'LuxKit/Pill/Pill'
import Caption from 'LuxKit/Typography/Caption'

interface Props {
  discountAmount: number
  discountPercentage?: number
  size: 'M' | 'L'
  onInfoIconClick?: MouseEventHandler<HTMLButtonElement>
}

const CaptionBlock = styled(Caption)`
  display: flex;
  flex-direction: row;
  gap: ${rem(4)};
  align-items: center;
`

const ValueAndDiscount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  white-space: nowrap;
  vertical-align: middle;
`

const InlineClickable = styled(Clickable)`
  display: inline-flex;
`

export default function CruisePriceSaveUpCaption({ discountAmount, discountPercentage, onInfoIconClick }: Props) {
  const handleInfoIconClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    onInfoIconClick?.(event)
  }, [onInfoIconClick])

  return <CaptionBlock variant="medium" colour="normal-contrast" weight="bold" wrap="no-wrap">
    <FormattedMessage
      id="mwxBXisHIo-q2qW9qLXG"
      defaultMessage="Save up to {price}"
      description="Cruise promotional price caption showing the maximum amount saved in currency"
      values={{
        price: <ValueAndDiscount>
          <FormatCurrency value={discountAmount} format="roundedDollar" />
          {!!discountPercentage && <Pill palette="success">
            <FormattedMessage
              id="SkgisH4bPxSGnL3fVogH"
              defaultMessage="-{percentage}%"
              description="Discount percentage displayed beside a cruise promotional savings amount"
              values={{ percentage: discountPercentage }}
            />
          </Pill>}
        </ValueAndDiscount>,
      }}
    />
    {onInfoIconClick && <InlineClickable onClick={handleInfoIconClick}>
      <LineInfoCircleIcon />
    </InlineClickable>}
  </CaptionBlock>
}
